import { Box, BoxProps, useTheme } from '@mui/material';
import { useEffect } from 'react';

import { RIGHT_DRAWER_WIDTH } from 'modules/common/components/RightDrawer/Drawer';
import MessageCenterDrawer from 'modules/IMC/MessageCenter/components/MessageCenterDrawer';
import { MessageCenterContextProvider } from 'modules/IMC/MessageCenter/context';
import { useIMCContext } from 'modules/IMC/common/context';
import InboxDrawer from 'modules/Inbox/InboxDrawer';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';

export default function RightSidebar({
    variant = 'persistent',
    disablePortal = false,
    slotProps = {},
    instanceId = 'global',
}: {
    variant?: 'persistent' | 'temporary';
    disablePortal?: boolean;
    slotProps?: {
        box?: BoxProps;
    };
    /**
     * Unique identifier for this RightSidebar instance.
     * - 'global': Layout's global RightSidebar
     * - 'dialog': RightSidebar inside FullScreenDialog
     */
    instanceId?: 'global' | 'dialog';
}) {
    const { currOpenDrawer, activeRightSidebarId, registerRightSidebar, unregisterRightSidebar } = useIMCContext();
    const theme = useTheme();
    const isProjectInboxEnabled = useStatsigGate(FeatureGates.ProjectInboxPoc);

    // Register this instance on mount, unregister on unmount
    useEffect(() => {
        registerRightSidebar(instanceId);
        return () => {
            unregisterRightSidebar(instanceId);
        };
    }, [instanceId, registerRightSidebar, unregisterRightSidebar]);

    // Only render if this instance is the active one OR if no instance is active yet (first render)
    // This prevents flickering during initial mount
    if (activeRightSidebarId !== null && activeRightSidebarId !== instanceId) {
        return null;
    }

    return (
        <Box
            {...slotProps.box}
            sx={{
                backgroundColor: 'cement.50',
                width: `${RIGHT_DRAWER_WIDTH}px`,
                display: 'none',
                transition: 'all 0.4s ease',
                ...(currOpenDrawer !== null && {
                    display: 'block',
                }),
                position: 'relative',
                ...(variant === 'temporary' && {
                    position: 'fixed',
                    top: '12px',
                }),
                [theme.breakpoints.up('md')]: {
                    borderRadius: 12,
                    paddingRight: {
                        xs: 0,
                        md: 1,
                    },
                    marginLeft: {
                        xs: 0,
                        md: 1,
                    },
                    marginTop: { xs: 0, md: 1 },
                },
                ...slotProps.box?.sx,
            }}>
            {isProjectInboxEnabled ? (
                <InboxDrawer drawerProps={{ variant, disablePortal }} />
            ) : (
                <MessageCenterContextProvider>
                    <MessageCenterDrawer drawerProps={{ variant, disablePortal }} />
                </MessageCenterContextProvider>
            )}
        </Box>
    );
}
